import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TContentModal,
  TNoticeModal,
  TDialogModal,
  TNotificationToast,
  TPageToast,
  TWallToast,
} from '@datatype/iyagigo/type';

interface IState {
  // ############### content modal ###############
  isContentModal: boolean;
  contentModalData: TContentModal | null;
  // ############### notice modal ###############
  isNoticeModal: boolean;
  noticeModalData: TNoticeModal | null;
  // ############### dialog modal ###############
  isDialogModal: boolean;
  dialogModalData: TDialogModal | null;
  // ############### notification toast ###############
  isNotificationToast: boolean;
  notificationToastData: TNotificationToast | null;
  // ############### page toast ###############
  isPageToast: boolean;
  pageToastData: TPageToast | null;
  // ############### wall toast ###############
  isWallToast: boolean;
  wallToastData: TWallToast | null;
  // ############### callout ###############
  isCallout: boolean;
}

const initialState: IState = {
  // ############### content modal ###############
  isContentModal: false,
  contentModalData: null,
  // ############### notice modal ###############
  isNoticeModal: false,
  noticeModalData: null,
  // ############### dialog modal ###############
  isDialogModal: false,
  dialogModalData: null,
  // ############### notification toast ###############
  isNotificationToast: false,
  notificationToastData: null,
  // ############### page toast ###############
  isPageToast: false,
  pageToastData: null,
  // ############### wall toast ###############
  isWallToast: false,
  wallToastData: null,
  // ############### callout ###############
  isCallout: false,
};

const hideModalState = (state: IState) => {
  state.isContentModal = false;
  state.isNoticeModal = false;
  state.isDialogModal = false;
  state.contentModalData = null;
  state.noticeModalData = null;
  state.dialogModalData = null;
};

const hideToastState = (state: IState) => {
  state.isNotificationToast = false;
  state.isPageToast = false;
  state.isWallToast = false;
  state.notificationToastData = null;
  state.pageToastData = null;
  state.wallToastData = null;
};

const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    initialize: (state) => {
      // auth 환영 모달(auth-complete)은 페이지 이동 시에도 보존
      // - root-app이 page 변경마다 modal/initialize 호출하는데, auth 흐름 직후 띄운 환영 모달이 즉시 사라지지 않도록
      const isUntouchedModal = state.dialogModalData?.type === 'auth-complete';
      const untouchedKeys = isUntouchedModal ? ['dialogModalData', 'isDialogModal'] : [];

      for (const key in state) {
        if (untouchedKeys.includes(key)) continue;
        state[key] = initialState[key];
      }
    },

    // ############### content modal ###############
    showContentModal: (state, action: PayloadAction<TContentModal>) => {
      state.isNoticeModal = false;
      state.isDialogModal = false;
      state.noticeModalData = null;
      state.dialogModalData = null;
      state.isContentModal = true;
      state.contentModalData = action.payload;
    },

    // ############### notice modal ###############
    showNoticeModal: (state, action: PayloadAction<TNoticeModal>) => {
      state.isContentModal = false;
      state.isDialogModal = false;
      state.contentModalData = null;
      state.dialogModalData = null;
      state.isNoticeModal = true;
      state.noticeModalData = action.payload;
    },

    // ############### dialog modal ###############
    showDialogModal: (state, action: PayloadAction<TDialogModal>) => {
      state.isContentModal = false;
      state.isNoticeModal = false;
      state.contentModalData = null;
      state.noticeModalData = null;
      state.isDialogModal = true;
      state.dialogModalData = action.payload;
    },

    hideModal: hideModalState,

    // ############### notification toast ###############
    showNotificationToast: (state, action: PayloadAction<TNotificationToast>) => {
      state.isNotificationToast = true;
      state.notificationToastData = action.payload;
    },

    // ############### page toast ###############
    showPageToast: (state, action: PayloadAction<TPageToast>) => {
      state.isPageToast = true;
      state.pageToastData = action.payload;
    },

    hideToast: hideToastState,

    hideOverlay: (state) => {
      hideModalState(state);
      hideToastState(state);
    },

    // ############### wall toast ###############
    showWallToast: (state, action: PayloadAction<TWallToast>) => {
      state.isWallToast = true;
      state.wallToastData = action.payload;
    },

    // ############### callout ###############
    showCallout: (state) => {
      state.isCallout = true;
    },
    hideCallout: (state) => {
      state.isCallout = false;
    },
  },
});

export const actions = modalSlice.actions;
export default modalSlice.reducer;
